import React from "react";
import {
    Box,
    Table,
    TableContainer,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
} from "@chakra-ui/react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";


type Column<T> = {
    key: string;
    header: React.ReactNode;
    render: (row: T, index: number) => React.ReactNode;
    isNumeric?: boolean;
    width?: string;
};

type AdminDataTableProps<T> = {
    columns: Column<T>[];
    rows: T[];
    rowKey: (row: T, index: number) => string;
    minW?: string;
    emptyText?: string;
    maxH?: string;
};

export function AdminDataTable<T>({
    columns,
    rows,
    rowKey,
    minW = "900px",
    emptyText = "No data found",
    maxH = "70vh",
}: AdminDataTableProps<T>) {
    const { theme } = useCustomTheme();
    const isDark = theme.name === "dark";

    const brand = theme.brand || {
        electricBlue: "#27b7ff",
        indigo: "#6f7cff",
        violet: "#b05cff",
    };

    const headerBg = isDark
        ? "rgba(255,255,255,0.02)"
        : "rgba(248,250,252,0.95)";

    const headerBorder = isDark
        ? "rgba(255,255,255,0.06)"
        : "rgba(15,23,42,0.06)";

    const cellBorderColor = isDark
        ? "rgba(255,255,255,0.045)"
        : "rgba(15,23,42,0.05)";

    // More colorful but still professional
    const oddRowBg = isDark
        ? "rgba(255,255,255,0.012)"
        : "rgba(2,6,23,0.015)";

    const evenRowBg = isDark
        ? "rgba(39,183,255,0.02)"
        : "rgba(39,183,255,0.025)";

    const hoverRowBg = isDark
        ? "rgba(111,124,255,0.10)"
        : "rgba(111,124,255,0.08)";

    const leftAccent = isDark
        ? `linear-gradient(180deg, ${brand.electricBlue}, ${brand.indigo})`
        : `linear-gradient(180deg, ${brand.electricBlue}, ${brand.violet})`;

    return (
        <Box
            borderRadius="20px"
            bg={theme.surface}
            boxShadow={theme.shadowMd}
            border={`1px solid ${theme.border}`}
            overflow="hidden"
            position="relative"
        >
            {/* subtle body glows (not header) */}
            <Box
                pointerEvents="none"
                position="absolute"
                top="56px"
                right="-40px"
                w="150px"
                h="150px"
                borderRadius="full"
                bg={isDark ? "rgba(39,183,255,0.07)" : "rgba(39,183,255,0.05)"}
                filter="blur(18px)"
                zIndex={0}
            />
            <Box
                pointerEvents="none"
                position="absolute"
                bottom="-55px"
                left="-40px"
                w="160px"
                h="160px"
                borderRadius="full"
                bg={isDark ? "rgba(176,92,255,0.07)" : "rgba(176,92,255,0.05)"}
                filter="blur(20px)"
                zIndex={0}
            />

            <TableContainer maxH={maxH} overflowY="auto" position="relative" zIndex={1}>
                <Table variant="unstyled" size="sm" minW={minW}>
                    <Thead position="sticky" top={0} zIndex={2}>
                        <Tr bg={headerBg} backdropFilter="blur(8px)">
                            {columns.map((col) => (
                                <Th
                                    key={col.key}
                                    isNumeric={col.isNumeric}
                                    w={col.width}
                                    px={4}
                                    py={3.5}
                                    color={theme.textMuted}
                                    fontSize="11px"
                                    fontWeight="800"
                                    letterSpacing="0.08em"
                                    textTransform="uppercase"
                                    borderBottom={`1px solid ${headerBorder}`}
                                    whiteSpace="nowrap"
                                >
                                    {col.header}
                                </Th>
                            ))}
                        </Tr>
                    </Thead>

                    <Tbody>
                        {rows.length === 0 ? (
                            <Tr>
                                <Td colSpan={columns.length} px={4} py={12} borderBottom="none">
                                    <Box
                                        borderRadius="16px"
                                        border={`1px dashed ${theme.border}`}
                                        bg={isDark ? "rgba(255,255,255,0.015)" : "rgba(255,255,255,0.65)"}
                                        py={8}
                                        textAlign="center"
                                    >
                                        <Text color={theme.textMuted} fontWeight="600">
                                            {emptyText}
                                        </Text>
                                    </Box>
                                </Td>
                            </Tr>
                        ) : (
                            rows.map((row, idx) => (
                                <Tr
                                    key={rowKey(row, idx)}
                                    bg={idx % 2 === 0 ? evenRowBg : oddRowBg}
                                    _hover={{ bg: hoverRowBg }}
                                    transition="all 0.18s ease"
                                >
                                    {columns.map((col, colIdx) => (
                                        <Td
                                            key={col.key}
                                            isNumeric={col.isNumeric}
                                            px={4}
                                            py={3.5}
                                            color={theme.text}
                                            borderBottom={`1px solid ${cellBorderColor}`}
                                            verticalAlign="middle"
                                            position="relative"
                                            _before={
                                                colIdx === 0
                                                    ? {
                                                        content: '""',
                                                        position: "absolute",
                                                        left: 0,
                                                        top: "8px",
                                                        bottom: "8px",
                                                        width: "3px",
                                                        borderRadius: "999px",
                                                        bg: idx % 2 === 0 ? leftAccent : "transparent",
                                                        opacity: 0.45,
                                                    }
                                                    : undefined
                                            }
                                        >
                                            {col.render(row, idx)}
                                        </Td>
                                    ))}
                                </Tr>
                            ))
                        )}
                    </Tbody>
                </Table>
            </TableContainer>
        </Box>
    );
}